import React, { useMemo } from "react";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { Link } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import SeoHelmet from "./components/SeoHelmet";
import FloatingWidgets from "./components/FloatingWidgets";

export default function Cart() {
  const { cartItems, updateCartQuantity, removeCartItem } = useAppContext();

  const subtotal = useMemo(() => {
    return cartItems.reduce((acc, item) => acc + item.price * item.quantity, 0);
  }, [cartItems]);

  return (
    <>
      <SeoHelmet
        title="Your Shopping Cart | A1 Bakery & Sweets"
        description="Review your selected treats and get ready to bring home joy. Secure checkout for all your favorite sweets and snacks."
        canonical={window.location.origin + "/cart"}
      />
      <Navbar />
      <header className="header-cart">
        <div className="header-content">
          <div className="breadcrumb__list mb-20">
            <span>
              <Link to="/">Home</Link>
            </span>
            <span className="dvdr">
              <i className="fa-regular fa-angle-right"></i>
            </span>
            <span>Cart</span>
          </div>
          <h1>Sweetness Awaits In Your Cart</h1>
          <p>Get Ready To Bring Home Joy, Bite By Bite.</p>
        </div>
      </header>
      <main className="cart-area pt-60 pb-50 bg-light">
        <div className="container">
          {cartItems.length > 0 ? (
            <div className="row g-4">
              {/* Cart Items List */}
              <div className="col-lg-8">
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4">
                  <div className="card-header bg-white p-4 border-0 border-bottom">
                    <h4 className="fw-bold mb-0">
                      Shopping Cart ({cartItems.length} items)
                    </h4>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th
                              className="ps-4 py-3 text-uppercase text-muted fw-bold"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Product
                            </th>
                            <th
                              className="py-3 text-uppercase text-muted fw-bold text-center"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Price
                            </th>
                            <th
                              className="py-3 text-uppercase text-muted fw-bold text-center"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Quantity
                            </th>
                            <th
                              className="py-3 text-uppercase text-muted fw-bold text-center"
                              style={{ fontSize: "0.75rem" }}
                            >
                              Subtotal
                            </th>
                            <th className="pe-4 py-3"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {cartItems.map((item, index) => (
                            <tr key={`${item.productId}_${item.net_wt}`}>
                              <td className="ps-4 py-4">
                                <div className="d-flex align-items-center gap-3">
                                  <Link
                                    to={`/product/${item.slug || item.productId}`}
                                    className="flex-shrink-0 border rounded-3 overflow-hidden"
                                    style={{ width: "120px", height: "80px" }}
                                  >
                                    <img
                                      src={item.img}
                                      alt={item.name}
                                      className="img-fluid h-100 w-100 object-fit-cover"
                                    />
                                  </Link>
                                  <div>
                                    <Link
                                      to={`/product/${item.slug || item.productId}`}
                                      className="text-dark fw-bold text-decoration-none d-block mb-1"
                                    >
                                      {item.name}
                                    </Link>
                                    <span className="badge bg-light border fw-normal">
                                      {item.net_wt}
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td className="text-center">
                                <span className="fw-medium text-dark">
                                  ₹{Math.round(Number(item.price))}
                                </span>
                              </td>
                              <td className="text-center">
                                <div className="d-inline-flex align-items-center border rounded-pill p-1 bg-light">
                                  <button
                                    className="btn btn-sm btn-white rounded-circle shadow-none border-0 p-0 d-flex align-items-center justify-content-center"
                                    style={{ width: "28px", height: "28px" }}
                                    type="button"
                                    onClick={() =>
                                      updateCartQuantity(index, -1)
                                    }
                                  >
                                    <i className="far fa-minus small"></i>
                                  </button>
                                  <input
                                    className="form-control form-control-sm border-0 bg-transparent text-center fw-bold p-0 mx-2 shadow-none"
                                    style={{ width: "30px" }}
                                    type="text"
                                    value={item.quantity}
                                    readOnly
                                  />
                                  <button
                                    className="btn btn-sm btn-white rounded-circle shadow-none border-0 p-0 d-flex align-items-center justify-content-center"
                                    style={{ width: "28px", height: "28px" }}
                                    type="button"
                                    onClick={() => updateCartQuantity(index, 1)}
                                  >
                                    <i className="far fa-plus small"></i>
                                  </button>
                                </div>
                              </td>
                              <td className="text-center">
                                <span className="fw-bold text-danger">
                                  ₹{Math.round(item.price * item.quantity)}
                                </span>
                              </td>
                              <td className="pe-4 text-end">
                                <button
                                  className="btn btn-link text-muted p-2"
                                  onClick={() => removeCartItem(index)}
                                >
                                  <i className="fa-regular fa-trash-can text-danger"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="card-footer bg-white p-4 border-0">
                    <Link
                      to="/categories"
                      className="text-decoration-none text-muted small fw-bold"
                    >
                      <i className="fa-solid fa-arrow-left me-2"></i> Continue
                      Shopping
                    </Link>
                  </div>
                </div>
              </div>

              {/* Order Summary Sidebar */}
              <div className="col-lg-4">
                <div
                  className="card border-0 shadow-sm rounded-4 sticky-top overflow-hidden"
                  style={{ top: "100px" }}
                >
                  <div className="card-body p-4">
                    <h5 className="fw-bold mb-4">Order Summary</h5>
                    <div className="d-flex justify-content-between mb-3 text-muted">
                      <span>Subtotal</span>
                      <span className="fw-bold text-dark">
                        ₹{Math.round(subtotal)}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mb-3 text-muted">
                      <span>Shipping</span>
                      <span className="small">Calculated at checkout</span>
                    </div>
                    <hr className="my-4 opacity-50" />
                    <div className="d-flex justify-content-between mb-4 align-items-center">
                      <span className="h5 fw-bold mb-0">Total Amount</span>
                      <h4 className="fw-bold text-danger mb-0">
                        ₹{Math.round(subtotal)}
                      </h4>
                    </div>
                    <Link
                      className="bd-fill__btn w-100 py-3 rounded-pill fw-bold mb-4 d-flex align-items-center justify-content-center"
                      to="/checkout"
                    >
                      Secure Checkout{" "}
                      <i className="fa-solid fa-lock ms-2 small"></i>
                    </Link>
                    <div className="text-center">
                      <p className="text-muted small mb-3">We Accept</p>
                      <div className="d-flex justify-content-center gap-3 fs-3">
                        <img src="/assets/img/a1-img/payment_icons/visa.png" alt="Visa" style={{ width: "40px", height: "30px", objectFit: "contain" }} />
                        <img src="/assets/img/a1-img/payment_icons/mastercard.png" alt="Mastercard" style={{ width: "40px", height: "30px", objectFit: "contain" }} />
                        <img src="/assets/img/a1-img/payment_icons/paypal.png" alt="Rupay" style={{ width: "40px", height: "30px", objectFit: "contain" }} />
                      </div>
                    </div>
                  </div>
                  <div className="bg-light p-3 text-center border-top">
                    <span className="small text-muted">
                      <i className="fa-solid fa-shield-halved text-success me-1"></i>{" "}
                      SSL Secure Payment
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="row justify-content-center py-5">
              <div className="col-lg-6 text-center">
                <img
                  className="img-fluid mb-4"
                  style={{ maxWidth: "280px" }}
                  src="/assets/img/a1-img/dash__elements/emptycart__img.png"
                  alt="Empty Cart"
                />
                <h2 className="fw-bold mb-3">Your Cart is Feeling Lonely</h2>
                <p className="text-muted mb-5 px-lg-5">
                  Add your favorite treats and turn today into a celebration!
                  Check out our signature collection to find something sweet.
                </p>
                <Link
                  to="/categories"
                  className="bm-hero__cta px-5 py-3 rounded-pill fw-bold text-decoration-none shadow"
                >
                  Shop Now
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>

      <FloatingWidgets />
      <Footer />
    </>
  );
}
